import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Search, Refresh, GetApp, ArrowBack, Close } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar, InputAdornment, TextField } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const fmtQty = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtPct = (n) => `${(parseFloat(n) || 0).toFixed(1)}%`;

const fmt2Date = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const PRIMARY = '#4a148c';
const PRIMARY_MID = '#8e24aa';
const PRIMARY_LT = '#f3e5f5';
const th = { padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, whiteSpace: 'nowrap', borderBottom: '1px solid rgba(255,255,255,0.2)' };
const td = { padding: '7px 10px', textAlign: 'left', fontSize: 12, whiteSpace: 'nowrap' };
const tdR = { padding: '7px 10px', textAlign: 'right', fontSize: 12, whiteSpace: 'nowrap' };

const iconBtn = {
  border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
  height: 29, width: 29, color: '#fff', flexShrink: 0,
  '&:hover': { background: 'rgba(255,255,255,0.1)' },
  '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

// Mini progress bar
const PctBar = ({ value, color }) => {
  const pct = Math.min(Math.max(parseFloat(value) || 0, 0), 100);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 90 }}>
      <div style={{ flex: 1, height: 6, background: '#e2e8f0', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${pct}%`, height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color, minWidth: 36, textAlign: 'right' }}>{fmtPct(pct)}</span>
    </div>
  );
};

const StockAnalysisReport = () => {
  useSessionValidation();
  const { companyId, branchId, token, companyName, username } = useAuth();
  const { logAudit } = useAuditLog();
  const history = useHistory();

  const today = new Date();
  const [fromDate, setFromDate] = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
  const [toDate, setToDate] = useState(fmt2Date(today));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fetched, setFetched] = useState(false);
  const [search, setSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);

  const fetchReport = async () => {
    if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
    setLoading(true);
    try {
      const res = await fetchData(
        'StockReportApi',
        `/StockAnalysis/${companyId}/${branchId}/${fromDate}/${toDate}`,
        token
      );
      const rows = res.response_data || res || [];
      setData(Array.isArray(rows) ? rows : []);
      setFetched(true);
    } catch (err) {
      console.error(err);
      setError('Failed to fetch stock analysis report');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (companyId && branchId && token) fetchReport();
  }, [companyId, branchId, token]); // eslint-disable-line

  const filtered = useMemo(() => {
    if (!search.trim()) return data;
    const q = search.toLowerCase();
    return data.filter(r => (r.ItemName || '').toLowerCase().includes(q));
  }, [data, search]);

  // Totals
  const totalOpening = filtered.reduce((s, r) => s + (parseFloat(r.OpeningQty) || 0), 0);
  const totalPurchase = filtered.reduce((s, r) => s + (parseFloat(r.PurchaseQty) || 0), 0);
  const totalPurRet = filtered.reduce((s, r) => s + (parseFloat(r.PurchaseReturnQty) || 0), 0);
  const totalSale = filtered.reduce((s, r) => s + (parseFloat(r.SaleQty) || 0), 0);
  const totalSaleRet = filtered.reduce((s, r) => s + (parseFloat(r.SaleReturnQty) || 0), 0);
  const totalTrIn = filtered.reduce((s, r) => s + (parseFloat(r.TransferInQty) || 0), 0);
  const totalTrOut = filtered.reduce((s, r) => s + (parseFloat(r.TransferOutQty) || 0), 0);
  const totalPrdIn = filtered.reduce((s, r) => s + (parseFloat(r.ProductionINQty) || 0), 0);
  const totalPrdOut = filtered.reduce((s, r) => s + (parseFloat(r.ProductionOutQty) || 0), 0);
  const totalIn = filtered.reduce((s, r) => s + (parseFloat(r.TotalInQty) || 0), 0);
  const totalOut = filtered.reduce((s, r) => s + (parseFloat(r.TotalOutQty) || 0), 0);
  const totalNet = filtered.reduce((s, r) => s + (parseFloat(r.NetQty) || 0), 0);
  const totalClosing = filtered.reduce((s, r) => s + (parseFloat(r.ClosingQty) || 0), 0);

  const handleExport = async () => {
    logAudit(AUDIT_ACTION.EXPORT, 'StockAnalysis', 0);
    if (!filtered.length) return;
    const cols = ['#', 'Item Name', 'Opening', 'Purchase', 'Pur.Return', 'Sale', 'Sale Return', 'Tr.In', 'Tr.Out', 'Production In', 'Production Out', 'Total In', 'Total Out', 'Net Qty', 'Closing', 'Purchase%', 'Sale%'];
    const rows = filtered.map((r, i) => [
      i + 1, r.ItemName,
      parseFloat(r.OpeningQty) || 0, parseFloat(r.PurchaseQty) || 0,
      parseFloat(r.PurchaseReturnQty) || 0, parseFloat(r.SaleQty) || 0,
      parseFloat(r.SaleReturnQty) || 0, parseFloat(r.TransferInQty) || 0,
      parseFloat(r.TransferOutQty) || 0, parseFloat(r.ProductionINQty) || 0,
      parseFloat(r.ProductionOutQty) || 0, parseFloat(r.TotalInQty) || 0,
      parseFloat(r.TotalOutQty) || 0, parseFloat(r.NetQty) || 0,
      parseFloat(r.ClosingQty) || 0,
      `${(parseFloat(r.PurchasePercent) || 0).toFixed(1)}%`,
      `${(parseFloat(r.SalePercent) || 0).toFixed(1)}%`,
    ]);

    await exportStyledExcel({
      reportTitle: 'STOCK ANALYSIS REPORT',
      sheetName: 'Stock Analysis',
      fileName: `StockAnalysisReport_${fromDate}_${toDate}.xlsx`,
      companyName,
      username,
      fromDate,
      toDate,
      headers: cols,
      rows,
      totalsRow: ['', 'Totals', totalOpening, totalPurchase, totalPurRet, totalSale, totalSaleRet, totalTrIn, totalTrOut, totalPrdIn, totalPrdOut, totalIn, totalOut, totalNet, totalClosing, '', ''],
      columnWidths: [4, 28, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12, 12],
    });
  };

  return (
    <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
      <LogoLoader open={loading} message="Loading stock analysis report..." />
      <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>

      <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

        {/* Toolbar */}
        <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, #6a1b9a 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
          <Tooltip title="Back" arrow>
            <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
              <ArrowBack sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>Stock Analysis Report</span>
          <div style={{ flex: 1, minWidth: 4 }} />
          {searchOpen ? (
            <>
              <TextField autoFocus size="small" placeholder="Search item..."
                value={search} onChange={e => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 15, color: 'rgba(255,255,255,0.6)' }} /></InputAdornment>,
                  endAdornment: search ? <InputAdornment position="end"><IconButton size="small" onClick={() => setSearch('')} sx={{ color: 'rgba(255,255,255,0.6)', p: '2px' }}><Close sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                }}
                sx={{ flexShrink: 0, width: 200, '& .MuiOutlinedInput-root': { height: 29, fontSize: 12, borderRadius: '8px', color: '#fff', '& fieldset': { borderColor: 'rgba(255,255,255,0.4)' }, '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.7)' } }, '& input::placeholder': { color: 'rgba(255,255,255,0.5)', opacity: 1 } }}
              />
              <Tooltip title="Close" arrow>
                <IconButton size="small" onClick={() => { setSearchOpen(false); setSearch(''); }} sx={iconBtn}>
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Tooltip title="Search" arrow>
              <IconButton size="small" onClick={() => setSearchOpen(true)} sx={iconBtn}>
                <Search sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
              <Refresh sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Export Excel" arrow>
            <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
              <GetApp sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </div>

        {/* Table */}
        <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 130px)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, minWidth: 1100 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 2 }}>
              <tr style={{ background: PRIMARY_MID, color: 'white' }}>
                <th style={th}>#</th>
                <th style={th}>Item Name</th>
                <th style={{ ...th, textAlign: 'right' }}>Opening</th>
                <th style={{ ...th, textAlign: 'right' }}>Purchase</th>
                <th style={{ ...th, textAlign: 'right' }}>Pur. Ret</th>
                <th style={{ ...th, textAlign: 'right' }}>Sale</th>
                <th style={{ ...th, textAlign: 'right' }}>Sale Ret</th>
                <th style={{ ...th, textAlign: 'right' }}>Tr. In</th>
                <th style={{ ...th, textAlign: 'right' }}>Tr. Out</th>
                <th style={{ ...th, textAlign: 'right' }}>Production In</th>
                <th style={{ ...th, textAlign: 'right' }}>Production Out</th>
                <th style={{ ...th, textAlign: 'right' }}>Total In</th>
                <th style={{ ...th, textAlign: 'right' }}>Total Out</th>
                <th style={{ ...th, textAlign: 'right' }}>Net Qty</th>
                <th style={{ ...th, textAlign: 'right' }}>Closing</th>
                <th style={{ ...th, textAlign: 'left', minWidth: 110 }}>Purchase %</th>
                <th style={{ ...th, textAlign: 'left', minWidth: 110 }}>Sale %</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={15} style={{ textAlign: 'center', padding: 24, color: '#94a3b8', fontSize: 13 }}>
                    {fetched ? 'No records found' : 'Select date range and click refresh'}
                  </td>
                </tr>
              ) : (
                filtered.map((r, i) => {
                  const closing = parseFloat(r.ClosingQty) || 0;
                  const net = parseFloat(r.NetQty) || 0;
                  return (
                    <tr key={r.ItemId || i} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT, borderBottom: '1px solid #f1f5f9' }}>
                      <td style={td}>{i + 1}</td>
                      <td style={{ ...td, fontWeight: 600, color: '#1e293b', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.ItemName}</td>
                      <td style={tdR}>{fmtQty(r.OpeningQty)}</td>
                      <td style={{ ...tdR, color: '#059669' }}>{fmtQty(r.PurchaseQty)}</td>
                      <td style={{ ...tdR, color: '#dc2626' }}>{fmtQty(r.PurchaseReturnQty)}</td>
                      <td style={{ ...tdR, color: '#dc2626' }}>{fmtQty(r.SaleQty)}</td>
                      <td style={{ ...tdR, color: '#059669' }}>{fmtQty(r.SaleReturnQty)}</td>
                      <td style={{ ...tdR, color: '#059669' }}>{fmtQty(r.TransferInQty)}</td>
                      <td style={{ ...tdR, color: '#dc2626' }}>{fmtQty(r.TransferOutQty)}</td>
                      <td style={{ ...tdR, fontWeight: 600, color: '#059669' }}>{fmtQty(r.ProductionINQty)}</td>
                      <td style={{ ...tdR, fontWeight: 600, color: '#dc2626' }}>{fmtQty(r.ProductionOutQty)}</td>
                      <td style={{ ...tdR, fontWeight: 600, color: '#059669' }}>{fmtQty(r.TotalInQty)}</td>
                      <td style={{ ...tdR, fontWeight: 600, color: '#dc2626' }}>{fmtQty(r.TotalOutQty)}</td>
                      <td style={{ ...tdR, fontWeight: 700, color: net >= 0 ? PRIMARY : '#dc2626' }}>{fmtQty(net)}</td>
                      <td style={{ ...tdR, fontWeight: 700, color: closing >= 0 ? PRIMARY : '#dc2626' }}>{fmtQty(closing)}</td>
                      <td style={{ padding: '7px 10px' }}><PctBar value={r.PurchasePercent} color="#059669" /></td>
                      <td style={{ padding: '7px 10px' }}><PctBar value={r.SalePercent} color="#dc2626" /></td>
                    </tr>
                  );
                })
              )}
            </tbody>
            {filtered.length > 0 && (
              <tfoot>
                <tr style={{ background: PRIMARY_LT, color: PRIMARY, fontWeight: 700 }}>
                  <td colSpan={2} style={{ ...td, textAlign: 'right', fontWeight: 700 }}>Totals</td>
                  <td style={tdR}>{fmtQty(totalOpening)}</td>
                  <td style={tdR}>{fmtQty(totalPurchase)}</td>
                  <td style={tdR}>{fmtQty(totalPurRet)}</td>
                  <td style={tdR}>{fmtQty(totalSale)}</td>
                  <td style={tdR}>{fmtQty(totalSaleRet)}</td>
                  <td style={tdR}>{fmtQty(totalTrIn)}</td>
                  <td style={tdR}>{fmtQty(totalTrOut)}</td>
                   <td style={tdR}>{fmtQty(totalPrdIn)}</td>
                  <td style={tdR}>{fmtQty(totalPrdOut)}</td>
                  <td style={tdR}>{fmtQty(totalIn)}</td>
                  <td style={tdR}>{fmtQty(totalOut)}</td>
                  <td style={tdR}>{fmtQty(totalNet)}</td>
                  <td style={tdR}>{fmtQty(totalClosing)}</td>
                  <td colSpan={2} style={td}></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
};

export default StockAnalysisReport;




